import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IEcoleQueryParams, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { ICoefficient, IMatiereCoeff, IMatiereEcole, defaultEcoleMatiere } from 'app/shared/model/matiere.model';
import { ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';

const initialState = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IMatiereEcole>,
  entity: defaultEcoleMatiere,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
  matiereWithCoeff: [] as ReadonlyArray<IMatiereCoeff>,
};

const apiUrl = 'api/matiere-ecoles';

const asArray = <T>(value: T[] | { content?: T[] } | null | undefined): T[] => {
  if (Array.isArray(value)) {
    return value;
  }
  const content = (value as { content?: T[] } | undefined | null)?.content;
  if (Array.isArray(content)) {
    return content;
  }
  return [];
};

// Actions

// export const getEntities = createAsyncThunk('matiere-ecoles/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
//   const requestUrl = `${apiUrl}?cacheBuster=${new Date().getTime()}`;
//   return axios.get<IMatiereEcole[]>(requestUrl);
// });

export const getEntities = createAsyncThunk(
  'matiere-ecoles-ecoleId/fetch_entity_list',
  async ({ ecoleId, page = 0, size = ITEMS_PER_PAGE }: IEcoleQueryParams) => {
    const requestUrl = `api/ecoles/${ecoleId}/matiere-ecoles?page=${page}&size=${size}`;
    const response = await axios.get<IMatiereEcole[]>(requestUrl);
    const totalItems = response.headers['x-total-count'];

    return { matiereList: asArray(response.data), totalItems };
  }
);

export const getEntity = createAsyncThunk(
  'matiere-ecoles/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IMatiereEcole>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getMatieresWithCoeffByClasseId = createAsyncThunk(
  'matiere-ecoles/fetch_entities_with_coeff__by_class',
  async (classId: string | number) => {
    const requestUrl = `${apiUrl}/classe/${classId}/coeff`;
    return axios.get<IMatiereCoeff[]>(requestUrl);
  }
);

export const updateCoefficient = createAsyncThunk(
  'matiere-ecoles/update_coefficient_entity',
  async (entity: ICoefficient, thunkAPI) => {
    const requestUrl = `api/coefficients/${entity.id}`;

    const result = await axios.put<ICoefficient>(requestUrl, cleanEntity(entity));
    thunkAPI.dispatch(getMatieresWithCoeffByClasseId(result.data.classeId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const createCoefficient = createAsyncThunk(
  'matiere-ecoles/create_coefficient_entity',
  async (entity: ICoefficient, thunkAPI) => {
    const requestUrl = `api/coefficients`;

    const result = await axios.post<ICoefficient>(requestUrl, cleanEntity(entity));
    thunkAPI.dispatch(getMatieresWithCoeffByClasseId(result.data.classeId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteCoefficient = createAsyncThunk(
  'matiere-ecoles/delete_coefficient_entity',
  async ({ id, classeId }: { id: string | number; classeId: string | number }, thunkAPI) => {
    const requestUrl = `api/coefficients/${id}`;
    const result = await axios.delete(requestUrl);
    thunkAPI.dispatch(getMatieresWithCoeffByClasseId(classeId));
    return result;
  },
  { serializeError: serializeAxiosError }
);
export const getMatieresByClasseId = createAsyncThunk('matiere/fetch_entity_list', async (id: string | number) => {
  const requestUrl = `${apiUrl}/classe/${id}`;
  return axios.get<IMatiereEcole[]>(requestUrl);
});

export const createEntity = createAsyncThunk(
  'matiere-ecoles/create_entity',
  async (entity: IMatiereEcole, thunkAPI) => {
    const result = await axios.post<IMatiereEcole>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({ ecoleId: entity.ecoleId, page: 0, size: ITEMS_PER_PAGE }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'matiere-ecoles/update_entity',
  async (entity: IMatiereEcole, thunkAPI) => {
    const result = await axios.put<IMatiereEcole>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({ ecoleId: entity.ecoleId, page: 0, size: ITEMS_PER_PAGE }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'matiere-ecoles/partial_update_entity',
  async (entity: IMatiereEcole, thunkAPI) => {
    const result = await axios.patch<IMatiereEcole>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({ ecoleId: entity.ecoleId, page: 0, size: ITEMS_PER_PAGE }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'matiere-ecoles/delete_entity',
  async ({ id, ecoleId }: { id: string | number; ecoleId: string }, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IMatiereEcole>(requestUrl);
    thunkAPI.dispatch(getEntities({ ecoleId, page: 0, size: ITEMS_PER_PAGE }));

    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice
export type MatiereEcoleState = Readonly<typeof initialState>;
export const MatiereEcoleSlice = createSlice({
  name: 'matiere-ecoles',
  initialState: initialState as MatiereEcoleState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.matiereList,
          totalItems: action.payload.totalItems,
        };
      })
      .addMatcher(isFulfilled(getMatieresByClasseId), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: asArray(action.payload.data),
        };
      })
      .addMatcher(isFulfilled(getMatieresWithCoeffByClasseId), (state, action) => {
        return {
          ...state,
          loading: false,
          matiereWithCoeff: action.payload.data,
        };
      })
      .addMatcher(
        isFulfilled(createEntity, updateEntity, partialUpdateEntity, createCoefficient, updateCoefficient, deleteCoefficient),
        (state, action) => {
          state.updating = false;
          state.loading = false;
          state.updateSuccess = true;
          if (action.payload?.data) {
            state.entity = action.payload.data;
          }
        }
      )
      .addMatcher(isPending(getEntities, getEntity, getMatieresWithCoeffByClasseId, getMatieresByClasseId), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(
        isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity, createCoefficient, updateCoefficient, deleteCoefficient),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.updating = true;
        }
      )
      .addMatcher(
        isRejected(
          getEntities,
          getEntity,
          getMatieresWithCoeffByClasseId,
          getMatieresByClasseId,
          createEntity,
          updateEntity,
          partialUpdateEntity,
          deleteEntity,
          createCoefficient,
          updateCoefficient,
          deleteCoefficient
        ),
        (state, action) => {
          state.loading = false;
          state.updating = false;
          state.updateSuccess = false;
          state.errorMessage = action.error.message;
        }
      );
  },
});

export const { reset } = MatiereEcoleSlice.actions;

// Reducer
export default MatiereEcoleSlice.reducer;
